import React from 'react'
import { RotateCcw } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import PlanSettings, { PLAN_SECTIONS } from '@/components/Calculations'
import { Button } from '@/components/ui/button'
import useStore, { PLAN_SLOTS, PLAN_SLOT_NAMES, visiblePlanKeys } from '@/stores/useStore'
import { arePlansEquivalent } from '@/lib/planSettings'
import { cn } from '@/lib/utils'
import { planDisplayName } from '@/lib/planDisplayNames'

export default function PlanInputComparison({ demoMode = false, toolbarActionsForPlan = null }) {
  const plans = useStore(useShallow((state) => PLAN_SLOTS.map((key) => state[key])))
  const resetPlanToBaseline = useStore((state) => state.resetPlanToBaseline)
  const visibleKeys = useStore((state) => visiblePlanKeys(state).join('|')).split('|').filter(Boolean)
  const [activeSection, setActiveSection] = React.useState('match')

  const visibleEntries = PLAN_SLOTS.flatMap((key, index) => (
    plans[index] && visibleKeys.includes(key) ? [{ key, plan: plans[index] }] : []
  ))

  const columns = `repeat(${visibleEntries.length}, minmax(22rem, 1fr))`

  return (
    <section aria-label="Plan settings comparison" className="flex h-full min-h-0 flex-col overflow-hidden border border-foreground/10 bg-background/35">
      <div className="flex min-h-0 flex-1">
        <aside className="w-44 shrink-0 overflow-y-auto border-r border-border bg-background p-1.5" aria-label="Plan settings sections">
          <nav className="space-y-0.5">
            {PLAN_SECTIONS.map((section) => {
              const selected = section.key === activeSection
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveSection(section.key)}
                  aria-current={selected ? 'page' : undefined}
                  className={cn(
                    'w-full rounded-md px-2.5 py-1.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    selected ? 'bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {section.label}
                </button>
              )
            })}
          </nav>
        </aside>

        <div className="min-h-0 min-w-0 flex-1 overflow-x-auto overscroll-contain">
          <div className="grid h-full min-w-full gap-px bg-border" style={{ gridTemplateColumns: columns }}>
            {visibleEntries.map((entry) => {
              const name = planDisplayName(entry.key, entry.plan.name || PLAN_SLOT_NAMES[entry.key])
              const matchesBaseline = entry.key !== 'planA' && arePlansEquivalent(plans[0], entry.plan)

              return (
                <article key={entry.key} aria-label={`${name} settings`} className="flex min-h-0 min-w-0 flex-col bg-card">
                  <header className="flex shrink-0 items-center gap-1 px-3 pb-2 pt-3">
                    <div className="min-w-0 flex-1 truncate text-sm font-semibold" title={name}>
                      <span>{name}</span>
                      {entry.key === 'planA' && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">Starting point</span>}
                      {matchesBaseline && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">Same as Current Plan</span>}
                    </div>
                    {toolbarActionsForPlan?.(entry.key)}
                    {entry.key !== 'planA' && (
                      <Button type="button" variant="ghost" size="icon-xs" disabled={matchesBaseline} onClick={() => resetPlanToBaseline(entry.key)} aria-label={`Restore ${name} from Current Plan`} title={matchesBaseline ? `${name} already matches Current Plan` : `Restore ${name} from Current Plan`}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </header>
                  <div className="min-h-0 flex-1">
                    <PlanSettings
                      demoMode={demoMode}
                      planKey={entry.key}
                      comparisonColumn
                      showToolbar={false}
                      activeSection={activeSection}
                      onSectionChange={setActiveSection}
                      singleSection
                    />
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
